// Import library React dan hooks yang diperlukan
import React, { useEffect, useState, useCallback, useRef } from 'react';
import LoadingOverlay from 'react-loading-overlay-ts';
import { useHistory } from "react-router-dom";
import axios from "../../utils/axios";
import { getToken, getUser, removeUserSession } from '../../utils/Common';
import Moment from 'moment';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import Layout from '../../components/Layout';
import {
  FileText,
  ClipboardList,
  Package,
  ShoppingCart,
  CheckCircle,
  TruckIcon,
  Filter,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';

/**
 * Komponen Dashboard Tailwind
 * Menampilkan statistik pembelian, forecast, grafik spending, dan progress dokumen per hotel
 * @returns {JSX.Element} Halaman dashboard lengkap
 */
function Dashboard() {
  // Hook untuk navigasi halaman
  const history = useHistory();

  // State untuk loading indicator
  const [loading, setLoading] = useState(false);

  // Mengambil data sesi user (encrypted)
  const userData = React.useMemo(() => getUser() || {}, []);
  const token = React.useMemo(() => getToken(), []);
  const headers = React.useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json'
  }), [token]);

  // Parse data sesi menjadi object
  const uID = userData.id; // ID user
  const hcode = userData.hotel_id; // ID hotel user
  const role = userData.role_name; // Nama role user
  const udeptID = userData.dept_id; // ID departemen user

  // State untuk menghitung jumlah dokumen pembelian
  const [countPP, setCountPP] = useState(0); // Jumlah Purchase Proposal
  const [countPR, setCountPR] = useState(0); // Jumlah Purchase Request
  const [countItem, setCountItem] = useState(0); // Jumlah Item
  const [countPOGenerate, setCountPOGenerate] = useState(0); // Jumlah PO yang digenerate
  const [countPOSent, setCountPOSent] = useState(0); // Jumlah PO yang dikirim
  const [countPORecord, setCountPORecord] = useState(0); // Jumlah PO yang direkam
  const [loadingForecastCards, setLoadingForecastCards] = useState(false); // Loading untuk kartu Forecast/Actual
  const forecastFetchInFlight = useRef(false);
  const [countItemReceive, setCountItemReceive] = useState(0); // Jumlah item yang diterima

  // State untuk Forecast & Actual Receive
  const [activeTab, setActiveTab] = useState('forecast'); // Tab aktif (forecast/actual)
  const [forecastActualData, setForecastActualData] = useState([]); // Data forecast dan aktual
  const [currentPage, setCurrentPage] = useState(1); // Halaman saat ini untuk pagination
  const itemsPerPage = 10; // Jumlah item per halaman
  const [selectedHotelFilter, setSelectedHotelFilter] = useState(''); // Filter hotel untuk grafik spending
  const [selectedDeptHotelFilter, setSelectedDeptHotelFilter] = useState(''); // Filter hotel untuk departemen
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState('');
  // Set default ke bulan terakhir yang ada data (Oktober 2026)
  // Jika bulan saat ini adalah November atau lebih baru, gunakan Oktober
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const defaultMonth = currentMonth;
  const defaultYear = 2026;
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth); // Bulan yang dipilih
  const [selectedYear, setSelectedYear] = useState(defaultYear); // Tahun yang dipilih

  // State for date range filters
  // Default: 1st of current month to end of current month
  const [startDate, setStartDate] = useState(() => {
    return Moment().startOf('month').format('YYYY-MM-DD');
  });
  const [endDate, setEndDate] = useState(() => {
    return Moment().endOf('month').format('YYYY-MM-DD');
  });

  // Pending filter states (for UI inputs before applying)
  const [tempSelectedHotelFilter, setTempSelectedHotelFilter] = useState('');
  const [tempSelectedDepartmentFilter, setTempSelectedDepartmentFilter] = useState('');
  const [tempStartDate, setTempStartDate] = useState(() => {
    return Moment().startOf('month').format('YYYY-MM-DD');
  });
  const [tempEndDate, setTempEndDate] = useState(() => {
    return Moment().endOf('month').format('YYYY-MM-DD');
  });

  const [hotelList, setHotelList] = useState([]); // Daftar hotel
  const [tableSearch, setTableSearch] = useState('');
  const matchSearch = (item, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    const fields = [
      item.id_pp,
      item.id_po,
      item.nomer,
      item.nama_item,
      item.companyname,
      item.nm_department,
      item.nm_hotel,
      item.quantity,
      item.unit,
      item.item_must_receive,
      item.date_need,
      item.pp_created_at,
      item.posent,
      item.quotation_price,
      item.quotation_total,
      item.head,
      item.receive_status,
      item.receive_item_date,
      item.receive_date,
      item.date_receive
    ];
    return fields.map(v => (v !== undefined && v !== null) ? String(v) : '').join(' ').toLowerCase().includes(s);
  };
  const normalizeReceiveDate = (item) =>
    item.receive_item_date || item.receive_date || item.date_receive;
  const isWithinRange = (dateStr, start, end) => {
    if (!dateStr || !start || !end) return true;
    const d = Moment(dateStr);
    const s = Moment(start);
    const e = Moment(end);
    if (!d.isValid() || !s.isValid() || !e.isValid()) return true;
    return d.isSameOrAfter(s, 'day') && d.isSameOrBefore(e, 'day');
  };

  // Function to export Forecast/Actual data to Excel
  const exportToExcel = () => {
    // Filter data based on active tab
    const filteredData = forecastActualData.filter(item => {
      const matchesTab = activeTab === 'forecast'
        ? item.receive_status == null
        : item.receive_status != null;
      const matchesDepartment =
        !selectedDepartmentFilter || item.nm_department === selectedDepartmentFilter;
      return matchesTab && matchesDepartment;
    });

    // Calculate total
    const total = filteredData.reduce((sum, item) => {
      const amount = item.quotation_total ? parseInt(item.quotation_total) : 0;
      return sum + amount;
    }, 0);

    // Get selected hotel name
    const selectedHotel = selectedHotelFilter
      ? hotelList.find(h => h.id_hotel == selectedHotelFilter)?.nm_hotel || 'All Hotels'
      : 'All Hotels';

    // Get selected department name
    const selectedDepartment = selectedDepartmentFilter || 'All Departments';

    // Create title and subtitle
    const title = `Purchase Order ${activeTab === 'forecast' ? 'Forecast' : 'Actual Receive'}`;
    const rangeStart = startDate ? Moment(startDate).format('DD MMM YYYY') : '';
    const rangeEnd = endDate ? Moment(endDate).format('DD MMM YYYY') : '';
    const dateRange = `${rangeStart} to ${rangeEnd}`;
    const subtitle = `Hotel: ${selectedHotel} | Department: ${selectedDepartment}`;

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows (rows 1-5)
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' }); // Empty row 1
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' }); // Empty row 2
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' }); // Title on row 3
    XLSX.utils.sheet_add_aoa(ws, [[dateRange]], { origin: 'A4' }); // Date range on row 4
    XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: 'A5' }); // Hotel & Dept on row 5

    // Prepare table headers (row 7)
    // Matching table columns: PO No., Item, Qty / Unit, Supplier, Est. Rec, Need, PP Created, PO Generated, Receive At, Price, Total, Note, Status
    const headers = [
      'PO No.', 'Item', 'Qty / Unit',
      'Supplier', 'Est. Rec', 'Need', 'PP Created',
      'PO Generated', ...(activeTab === 'actual' ? ['Receive At'] : []), 'Price', 'Total', 'Note', 'Status'
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });

    // Prepare data rows (starting from row 8)
    const dataRows = filteredData.map(item => {
      // Parse quantity field to split number and unit
      let quantity = '0';
      let unit = '';

      // First try to get unit from separate unit field
      if (item.unit && typeof item.unit === 'string') {
        unit = item.unit.trim();
        // If unit field exists, use quantity field as just the number
        quantity = item.quantity ? item.quantity.toString().trim() : '0';
      } else if (item.quantity) {
        // Try to parse quantity field that contains both number and unit
        const quantityStr = item.quantity.toString().trim();
        const match = quantityStr.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
        if (match) {
          quantity = match[1];
          unit = match[2];
        } else {
          // If no unit found, treat whole string as quantity
          quantity = quantityStr;
          unit = '';
        }
      }

      // Combine Qty / Unit
      const qtyUnit = `${quantity} ${unit}`.trim();

      // Combine PO No, Department, and Hotel with line breaks
      const poInfo = `${item.nomer || ''}\n${item.nm_department || '-'}\n${item.nm_hotel || '-'}`;

      const row = [
        poInfo,
        item.nama_item || '',
        qtyUnit,
        item.companyname || '-',
        item.item_must_receive ? Moment(item.item_must_receive).format('DD MMM YYYY') : '',
        item.date_need ? Moment(item.date_need).format('DD MMM YYYY') : '',
        item.pp_created_at ? Moment(item.pp_created_at).format('DD MMM YYYY') : '',
        item.created_at ? Moment(item.created_at).format('DD MMM YYYY') : ''
      ];

      if (activeTab === 'actual') {
        const receiveDate = item.receive_item_date || item.receive_date || item.date_receive;
        row.push(receiveDate ? Moment(receiveDate).format('DD MMM YYYY') : '');
      }

      row.push(
        item.quotation_price ? parseInt(item.quotation_price).toLocaleString('id-ID') : '0',
        item.quotation_total ? parseInt(item.quotation_total).toLocaleString('id-ID') : '0',
        item.head || '',
        item.receive_status ? (
          item.receive_status == 1 ? 'Full Receive' :
            item.receive_status == 2 ? 'Half Receive' :
              item.receive_status == 3 ? 'Defect Receive' :
                item.receive_status == 4 ? 'Reject' : 'Waiting'
        ) : 'Waiting for Received'
      );
      
      return row;
    });

    // Add data rows
    XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

    // Add total row
    const totalRow = dataRows.length + 8;
    // Align TOTAL under 'Price' (index 8/9) and Value under 'Total' (index 9/10)
    // Columns: 0:PO, 1:Item, 2:QtyUnit, 3:Supplier, 4:EstRec, 5:Need, 6:PPCreated, 7:PO Generated, [8:ReceiveAt], Price, Total, Note, Status
    const priceColIndex = activeTab === 'actual' ? 9 : 8;
    const totalColIndex = activeTab === 'actual' ? 10 : 9;
    
    const totalRowData = new Array(priceColIndex + 1).fill('');
    totalRowData[priceColIndex] = 'TOTAL';
    totalRowData[totalColIndex] = total.toLocaleString('id-ID');
    
    XLSX.utils.sheet_add_aoa(ws, [totalRowData], { origin: `A${totalRow}` });

    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // PO No. (wider for combined info)
      { wch: 30 }, // Item
      { wch: 15 }, // Qty / Unit
      { wch: 25 }, // Supplier
      { wch: 15 }, // Est. Rec
      { wch: 15 }, // Need
      { wch: 15 }, // PP Created
      { wch: 15 }, // PO Generated
      ...(activeTab === 'actual' ? [{ wch: 15 }] : []), // Receive At
      { wch: 15 }, // Price
      { wch: 20 }, // Total
      { wch: 25 }, // Note
      { wch: 15 }  // Status
    ];

    // Apply borders and styling to all table cells
    const range = XLSX.utils.decode_range(ws['!ref']);

    // Create border style object
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Apply styling to each cell in the table
    for (let R = 6; R <= totalRow - 1; R++) { // Start from row 7 (index 6)
      for (let C = range.s.c; C <= (activeTab === 'actual' ? 12 : 11); C++) { // columns count based on tab
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });

        // Initialize cell if it doesn't exist
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }

        // Initialize style object
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }

        // Add borders
        ws[cellAddress].s.border = borderStyle;
        ws[cellAddress].s.alignment = {
          vertical: 'top',
          horizontal: (C >= priceColIndex && C <= totalColIndex) ? 'right' : 'left', // Price and Total right aligned
          wrapText: true
        };

        // Style header row (row 7 = index 6)
        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = {
            patternType: 'solid',
            fgColor: { rgb: 'E0E0E0' }
          };
          ws[cellAddress].s.alignment = {
            vertical: 'center',
            horizontal: 'center',
            wrapText: true
          };
        }

        // Style total row
        if (R === totalRow - 1) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = {
            patternType: 'solid',
            fgColor: { rgb: 'FFEB9C' }
          };
        }
      }
    }

    // Style title rows (no borders, just formatting)
    ['A3', 'A4', 'A5'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: {
            bold: idx === 0,
            sz: [14, 12, 11][idx]
          },
          alignment: {
            vertical: 'center',
            horizontal: 'left'
          }
        };
      }
    });

    // Append sheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, activeTab === 'forecast' ? 'Forecast' : 'Actual Receive');

    // Generate filename
    const fileName = `PO_${activeTab === 'forecast' ? 'Forecast' : 'Actual_Receive'}_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;

    // Download file
    XLSX.writeFile(wb, fileName);
  };

  // Function to export Department Spending to Excel
  const exportDepartmentSpending = () => {
    // Calculate total
    const total = topSpendDepartments.reduce((sum, dept) => sum + dept.spent, 0);

    // Get selected hotel name
    const selectedHotel = selectedDeptHotelFilter
      ? hotelList.find(h => h.id_hotel == selectedDeptHotelFilter)?.nm_hotel || 'All Hotels'
      : 'All Hotels';

    // Create title
    const title = 'Department Spending Report';
    const dateRange = `${new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'long' })} ${selectedYear}`;
    const subtitle = `Hotel: ${selectedHotel} | Type: All orders (forecast + actual)`;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[dateRange]], { origin: 'A4' });
    XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: 'A5' });

    // Table headers (row 7)
    const headers = ['Department', 'Hotel', 'Orders Count', 'Total Spending (IDR)', 'Level'];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });

    // Data rows
    const dataRows = topSpendDepartments.map(dept => [
      dept.name || '',
      dept.hotel || '-',
      dept.orders || 0,
      dept.spent ? dept.spent.toLocaleString('id-ID') : '0',
      dept.level === 'high' ? 'High Spender' : dept.level === 'medium' ? 'Medium Spender' : 'Low Spender'
    ]);

    XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

    // Add total row
    const totalRow = dataRows.length + 8;
    const totalRowData = ['', '', 'TOTAL', total.toLocaleString('id-ID'), ''];
    XLSX.utils.sheet_add_aoa(ws, [totalRowData], { origin: `A${totalRow}` });

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Department
      { wch: 25 }, // Hotel
      { wch: 15 }, // Orders
      { wch: 20 }, // Spending
      { wch: 20 }  // Level
    ];

    // Apply borders and styling
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    for (let R = 6; R <= totalRow - 1; R++) {
      for (let C = 0; C <= 4; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }

        ws[cellAddress].s.border = borderStyle;
        ws[cellAddress].s.alignment = {
          vertical: 'center',
          horizontal: C >= 2 ? 'right' : 'left',
          wrapText: true
        };

        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'E0E0E0' } };
          ws[cellAddress].s.alignment = { vertical: 'center', horizontal: 'center', wrapText: true };
        }

        if (R === totalRow - 1) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'FFEB9C' } };
        }
      }
    }

    // Style title rows
    ['A3', 'A4', 'A5'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: idx === 0, sz: [14, 12, 11][idx] },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Department Spending');
    const fileName = `Department_Spending_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Function to export specific hotel's top items
  const exportHotelTopItems = (hotelId, hotelCode, hotelName) => {
    // Filter items for specific hotel
    const hotelItems = topOrderedItems.filter(item => {
      if (!hotelId) return true; // If no hotel specified, export all
      return item.hotel_id == hotelId || item.hotel === hotelName;
    });

    // Calculate total
    const total = hotelItems.reduce((sum, item) => sum + (item.value || 0), 0);

    // Create title
    const title = `Top 5 Items - ${hotelCode || hotelName || 'All Hotels'}`;
    const dateRange = `${new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'long' })} ${selectedYear}`;
    const subtitle = `Hotel: ${hotelName || 'All Hotels'} | Based on received items (sorted by total spending)`;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[dateRange]], { origin: 'A4' });
    XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: 'A5' });

    // Table headers (row 7)
    const headers = ['Rank', 'Item Name', 'Quantity', 'Unit', 'Avg Price (IDR)', 'Order Count', 'Total Value (IDR)'];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });

    // Data rows (top 5 items)
    const top5Items = hotelItems.slice(0, 5);
    const dataRows = top5Items.map((item, index) => [
      index + 1,
      item.name || '',
      item.stock || 0,
      item.unit || 'units',
      item.avgPrice ? item.avgPrice.toLocaleString('id-ID') : '0',
      item.orderCount || 0,
      item.value ? item.value.toLocaleString('id-ID') : '0'
    ]);

    XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

    // Add total row
    const totalRow = dataRows.length + 8;
    const totalRowData = ['', '', '', '', '', 'TOTAL', total.toLocaleString('id-ID')];
    XLSX.utils.sheet_add_aoa(ws, [totalRowData], { origin: `A${totalRow}` });

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // Rank
      { wch: 35 }, // Item Name
      { wch: 12 }, // Quantity
      { wch: 10 }, // Unit
      { wch: 18 }, // Avg Price
      { wch: 15 }, // Order Count
      { wch: 20 }  // Total Value
    ];

    // Apply borders and styling
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    for (let R = 6; R <= totalRow - 1; R++) {
      for (let C = 0; C <= 6; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }

        ws[cellAddress].s.border = borderStyle;
        ws[cellAddress].s.alignment = {
          vertical: 'center',
          horizontal: C >= 2 ? 'right' : 'left',
          wrapText: true
        };

        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'E0E0E0' } };
          ws[cellAddress].s.alignment = { vertical: 'center', horizontal: 'center', wrapText: true };
        }

        if (R === totalRow - 1) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'FFEB9C' } };
        }
      }
    }

    // Style title rows
    ['A3', 'A4', 'A5'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: idx === 0, sz: [14, 12, 11][idx] },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Top 5 Items');
    const fileName = `Top_5_Items_${hotelCode || 'All'}_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Function to export Top Spend Items to Excel
  const exportTopSpendItems = () => {
    // Calculate total
    const total = topOrderedItems.reduce((sum, item) => sum + (item.value || 0), 0);

    // Get selected hotel name
    const selectedHotel = selectedHotelFilter
      ? hotelList.find(h => h.id_hotel == selectedHotelFilter)?.nm_hotel || 'All Hotels'
      : 'All Hotels';

    // Create title
    const title = 'Top Spend Items Report';
    const dateRange = `${new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'long' })} ${selectedYear}`;
    const subtitle = `Hotel: ${selectedHotel} | Based on received items (sorted by total spending)`;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[dateRange]], { origin: 'A4' });
    XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: 'A5' });

    // Table headers (row 7)
    const headers = ['Item Name', 'Hotel', 'Quantity', 'Unit', 'Avg Price (IDR)', 'Order Count', 'Total Value (IDR)'];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });

    // Data rows
    const dataRows = topOrderedItems.map(item => [
      item.name || '',
      item.hotel || '-',
      item.stock || 0,
      item.unit || 'units',
      item.avgPrice ? item.avgPrice.toLocaleString('id-ID') : '0',
      item.orderCount || 0,
      item.value ? item.value.toLocaleString('id-ID') : '0'
    ]);

    XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

    // Add total row
    const totalRow = dataRows.length + 8;
    const totalRowData = ['', '', '', '', '', 'TOTAL', total.toLocaleString('id-ID')];
    XLSX.utils.sheet_add_aoa(ws, [totalRowData], { origin: `A${totalRow}` });

    // Set column widths
    ws['!cols'] = [
      { wch: 35 }, // Item Name
      { wch: 25 }, // Hotel
      { wch: 12 }, // Quantity
      { wch: 10 }, // Unit
      { wch: 18 }, // Avg Price
      { wch: 15 }, // Order Count
      { wch: 20 }  // Total Value
    ];

    // Apply borders and styling
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    for (let R = 6; R <= totalRow - 1; R++) {
      for (let C = 0; C <= 6; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }

        ws[cellAddress].s.border = borderStyle;
        ws[cellAddress].s.alignment = {
          vertical: 'center',
          horizontal: C >= 2 ? 'right' : 'left',
          wrapText: true
        };

        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'E0E0E0' } };
          ws[cellAddress].s.alignment = { vertical: 'center', horizontal: 'center', wrapText: true };
        }

        if (R === totalRow - 1) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'FFEB9C' } };
        }
      }
    }

    // Style title rows
    ['A3', 'A4', 'A5'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: idx === 0, sz: [14, 12, 11][idx] },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Top Spend Items');
    const fileName = `Top_Spend_Items_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const [modalImage, setModalImage] = useState(null); // State untuk modal gambar
  const [isLoading, setIsLoading] = useState(true); // State loading
  const [hoveredPoint, setHoveredPoint] = useState(null); // State untuk tracking hover point di chart
  const [fallbackOrders, setFallbackOrders] = useState([]); // Fallback data from PO endpoint (for Actual search)

  /**
   * Fungsi untuk mengambil data dashboard utama
   * Mengambil jumlah PP, PR, PO, dan Item dari server
   */
  const fetchDashboardData = useCallback(async () => {
    console.log('🔄 fetchDashboardData - Start fetching...', { hcode, udeptID, hasHeaders: !!headers });
    setLoading(true);
    setIsLoading(true);

    try {
      // Kirim request ke API untuk mendapatkan data dashboard
      console.log('📤 fetchDashboardData - Sending request to API:', {
        url: '/dashboard/data',
        payload: { hcode, udeptID },
        headers: headers ? 'Present' : 'Missing'
      });

      const response = await axios.post('/dashboard/data',
        { hcode, udeptID },
        { headers }
      );

      console.log('📥 fetchDashboardData - API Response:', {
        status: response.status,
        dataStatus: response.data?.status,
        fullResponse: response.data,
        data: response.data?.data,
        counts: response.data?.data?.counts
      });

      if (response.data && response.data.status === 200) {
        const data = response.data.data || {};
        const counts = data.counts || {};

        console.log('✅ fetchDashboardData - Valid response, updating counters:', {
          pp_count: counts.pp_count || 0,
          pr_count: counts.pr_count || 0,
          item_count: counts.item_count || 0,
          po_generate_count: counts.po_generate_count || 0,
          po_record_count: counts.po_record_count || 0,
          po_sent_count: counts.po_sent_count || 0,
          item_receive_count: counts.item_receive_count || 0
        });

        // Update semua state counter dengan data dari API
        setCountPP(counts.pp_count || 0);
        setCountPR(counts.pr_count || 0);
        setCountItem(counts.item_count || 0);
        setCountPOGenerate(counts.po_generate_count || 0);
        setCountPORecord(counts.po_record_count || 0);
        setCountPOSent(counts.po_sent_count || 0);
        setCountItemReceive(counts.item_receive_count || 0);
      } else {
        // Jika response tidak sukses, set semua ke 0
        console.warn('⚠️ fetchDashboardData - Invalid response status:', {
          responseStatus: response?.status,
          dataStatus: response?.data?.status,
          message: response?.data?.message,
          fullResponse: response?.data
        });

        setCountPP(0);
        setCountPR(0);
        setCountItem(0);
        setCountPOGenerate(0);
        setCountPORecord(0);
        setCountPOSent(0);
        setCountItemReceive(0);
      }
    } catch (error) {
      // Jika error, set semua counter ke 0
      console.error('❌ fetchDashboardData - Error occurred:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error
      });

      setCountPP(0);
      setCountPR(0);
      setCountItem(0);
      setCountPOGenerate(0);
      setCountPORecord(0);
      setCountPOSent(0);
      setCountItemReceive(0);
    } finally {
      // Matikan loading indicator
      console.log('🏁 fetchDashboardData - Finished, setting loading to false');
      setLoading(false);
      setIsLoading(false);
    }
  }, [hcode, udeptID, headers]);

  /**
   * Fungsi untuk mengambil daftar hotel berdasarkan user
   * Digunakan untuk populate filter hotel
   */
  const getListHotelbyUser = useCallback(() => {
    axios
      .post('/list/hotel/by-user', { hcode }, { headers })
      .then(function (response) {
        if (response.data && Array.isArray(response.data)) {
          // Flatten nested array menjadi flat array
          const flattenedHotels = response.data.flat();
          setHotelList(flattenedHotels);
        } else {
          setHotelList([]);
        }
      })
      .catch(function (error) {
        // Jika error, set array kosong
        setHotelList([]);
      });
  }, [hcode, headers]);

  /**
   * Fungsi untuk mengambil data forecast dan aktual receive
   * Data difilter berdasarkan hotel, bulan, dan tahun yang dipilih
   */
  const getForecastActualData = useCallback(() => {
    setLoadingForecastCards(true);
    console.log('🔄 getForecastActualData - Start fetching...', {
      hcode,
      hotel_filter: selectedHotelFilter,
      month: selectedMonth,
      year: selectedYear,
      hasHeaders: !!headers
    });

    const requestData = {
      hcode,
      hotel_filter: selectedHotelFilter,
      month: selectedMonth,
      year: selectedYear
    };

    console.log('📤 getForecastActualData - Sending request to API:', {
      url: '/forecast/actual/data',
      payload: requestData,
      headers: headers ? 'Present' : 'Missing'
    });

    axios
      .post('/forecast/actual/data', requestData, { headers })
      .then(function (response) {
        console.log('📥 getForecastActualData - API Response:', {
          status: response.status,
          dataStatus: response.data?.status,
          hasData: !!response.data?.data,
          dataLength: response.data?.data?.length || 0,
          fullResponse: response.data
        });

        if (response.data && response.data.data) {
          console.log('✅ getForecastActualData - Valid response, setting forecast data:', {
            dataLength: response.data.data.length,
            firstItem: response.data.data[0]
          });
          setForecastActualData(response.data.data);
        } else {
          console.warn('⚠️ getForecastActualData - No data in response, setting empty array');
          setForecastActualData([]);
        }
      })
      .catch(function (error) {
        console.error('❌ getForecastActualData - Error occurred:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          fullError: error
        });
        setForecastActualData([]);
      })
      .finally(() => {
        setLoadingForecastCards(false);
      });
  }, [hcode, selectedHotelFilter, selectedMonth, selectedYear, headers]);

  // Fetch top ordered items
  const getTopOrderedItems = useCallback(() => {
    console.log('🔄 getTopOrderedItems - Start fetching...', {
      hcode,
      hotel_filter: selectedHotelFilter,
      month: selectedMonth,
      year: selectedYear,
      hasHeaders: !!headers
    });

    if (!hcode || !headers) {
      console.warn('⚠️ getTopOrderedItems - Missing required parameters:', {
        hcode: !!hcode,
        headers: !!headers
      });
      return;
    }

    const requestData = {
      hcode,
      hotel_filter: selectedHotelFilter,
      month: selectedMonth,
      year: selectedYear
    };

    console.log('📤 getTopOrderedItems - Sending request to API:', {
      url: '/top/ordered/items',
      payload: requestData,
      headers: headers ? 'Present' : 'Missing'
    });

    axios
      .post('/top/ordered/items', requestData, { headers })
      .then(function (response) {
        console.log('📥 getTopOrderedItems - API Response:', {
          status: response.status,
          dataStatus: response.data?.status,
          hasData: !!response.data?.data,
          isArray: Array.isArray(response.data?.data),
          dataLength: response.data?.data?.length || 0,
          fullResponse: response.data
        });

        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Transform API data to match component structure
          const transformedItems = response.data.data.map((item, index) => {
            return {
              id: item.id_item,
              latest_pp_id: item.latest_pp_id,
              name: item.nama_item || 'Unknown Item',
              stock: parseInt(item.total_quantity) || 0,
              value: parseInt(item.total_value) || 0,
              avgPrice: parseInt(item.avg_price) || 0,
              image: item.item_image || null,
              low: item.low_stock || false,
              unit: item.unit || 'units',
              hotel: item.nm_hotel || '',
              orderCount: item.order_count || 0
            };
          });

          console.log('✅ getTopOrderedItems - Valid response, transforming and setting items:', {
            originalLength: response.data.data.length,
            transformedLength: transformedItems.length,
            firstItem: transformedItems[0],
            allItems: transformedItems
          });

          setTopOrderedItems(transformedItems);
        } else {
          console.warn('⚠️ getTopOrderedItems - Invalid response format:', {
            hasData: !!response.data?.data,
            isArray: Array.isArray(response.data?.data),
            responseData: response.data
          });
        }
      })
      .catch(function (error) {
        console.error('❌ getTopOrderedItems - Error occurred:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          fullError: error
        });
        // Keep default items on error
      });
  }, [hcode, selectedHotelFilter, selectedMonth, selectedYear, headers]);

  // Fetch top spending departments
  const getTopSpendDepartments = useCallback(() => {
    if (!hcode || !headers) {
      return;
    }

    const requestData = {
      hcode,
      hotel_filter: selectedDeptHotelFilter,
      month: selectedMonth,
      year: selectedYear
    };

    axios
      .post('/top/spend/departments', requestData, { headers })
      .then(function (response) {
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Transform API data to match component structure
          const transformedDepartments = response.data.data.map(dept => ({
            name: dept.nm_department || 'Unknown Department',
            orders: parseInt(dept.total_orders) || 0,
            spent: parseInt(dept.total_spent) || 0,
            level: dept.spending_level || 'low',
            hotel: dept.nm_hotel || '',
            code: dept.code_department || '',
            avgOrder: parseInt(dept.avg_order_value) || 0,
            items: parseInt(dept.total_items) || 0
          }));

          setTopSpendDepartments(transformedDepartments);
        }
      })
      .catch(function (error) {
        console.error('Error fetching top spend departments:', error);
        // Keep default departments on error
      });
  }, [hcode, selectedDeptHotelFilter, selectedMonth, selectedYear, headers]);

  // Fetch spending statistics for chart
  const getSpendingStatistics = useCallback(() => {
    if (!hcode || !headers) {
      return;
    }

    const requestData = {
      hcode,
      month: selectedMonth,
      year: selectedYear
    };

    axios
      .post('/spending/statistics', requestData, { headers })
      .then(function (response) {
        if (response.data && response.data.chart_data && response.data.summary) {
          setSpendingStatistics({
            chartData: response.data.chart_data,
            summary: response.data.summary
          });
        } else {
          // Set some default data for testing
          setSpendingStatistics({
            chartData: {
              labels: ['AS1', 'AS2', 'JU1'],
              datasets: [{
                data: [15000000, 12000000, 8000000],
                backgroundColor: ['rgba(147, 51, 234, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)']
              }]
            },
            summary: {
              total_spending: 35000000,
              total_hotels: 3,
              period: 'Test Data'
            }
          });
        }
      })
      .catch(function (error) {
        // Keep default data on error
      });
  }, [hcode, selectedMonth, selectedYear, headers]);

  // Fetch forecast and actual receive data
  const fetchForecastActualData = useCallback(async () => {
    if (forecastFetchInFlight.current) {
      return;
    }
    forecastFetchInFlight.current = true;
    setLoadingForecastCards(true);
    console.log('🔄 fetchForecastActualData - Start fetching...', {
      hcode,
      selected_hotel: selectedHotelFilter,
      startDate,
      endDate,
      hasHeaders: !!headers
    });

    try {
      // Use startDate and endDate from state
      if (!startDate || !endDate) {
        console.warn('⚠️ fetchForecastActualData - Missing date range, skipping fetch');
        return;
      }

      const requestData = {
        hcode: hcode, // Always send user's session hotel(s)
        selected_hotel: selectedHotelFilter, // Send selected hotel filter separately
        start_date: startDate,
        end_date: endDate,
        tab: activeTab // Send active tab to backend
      };

      console.log('📤 fetchForecastActualData - Sending request to API:', {
        url: '/forecast/actual/data',
        payload: requestData,
        dateRange: { startDate, endDate },
        activeTab: activeTab,
        headers: headers ? 'Present' : 'Missing'
      });

      const response = await axios.post('/forecast/actual/data',
        requestData,
        { headers }
      );

      console.log('📥 fetchForecastActualData - API Response:', {
        status: response.status,
        dataStatus: response.data?.status,
        hasData: !!response.data?.data,
        dataLength: response.data?.data?.length || 0,
        fullResponse: response.data
      });

      if (response.data && response.data.status === 200) {
        console.log('✅ fetchForecastActualData - Valid response, setting forecast data:', {
          dataLength: response.data.data?.length || 0,
          firstItem: response.data.data?.[0]
        });
        setForecastActualData(response.data.data || []);
      } else {
        console.warn('⚠️ fetchForecastActualData - Invalid response status:', {
          responseStatus: response?.status,
          dataStatus: response?.data?.status,
          message: response?.data?.message,
          fullResponse: response?.data
        });
        setForecastActualData([]);
      }
    } catch (error) {
      console.error('❌ fetchForecastActualData - Error occurred:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        fullError: error
      });
      setForecastActualData([]);
    } finally {
      console.log('🏁 fetchForecastActualData - Finished');
      setLoadingForecastCards(false);
      forecastFetchInFlight.current = false;
    }
  }, [hcode, headers, selectedHotelFilter, startDate, endDate, activeTab]);

  // Initial data load
  useEffect(() => {
    console.log('📅 Dashboard - Initial load with date selection:', {
      selectedMonth,
      selectedYear
    });

    if (token && hcode) {
      fetchDashboardData();
      getListHotelbyUser();
      // Don't load forecast data initially, wait for hotels to load first
    } else {
      setIsLoading(false);
      setLoading(false);
    }
  }, []); // Only run once on mount

  // Load forecast data after hotels are loaded
  useEffect(() => {
    if (token && hcode && hotelList.length > 0) {
      fetchForecastActualData();
      getTopOrderedItems();
      getTopSpendDepartments();
      getSpendingStatistics();
    }
  }, [hotelList.length]); // Run when hotels are loaded

  // Refetch forecast data when filters change
  // Separate useEffect for Top Spend Items (uses selectedHotelFilter)
  useEffect(() => {
    if (token && hcode && hotelList.length > 0) {
      getTopOrderedItems();
    }
  }, [selectedHotelFilter, selectedMonth, selectedYear]);

  // Separate useEffect for Top Spend Department (uses selectedDeptHotelFilter)
  useEffect(() => {
    if (token && hcode && hotelList.length > 0) {
      getTopSpendDepartments();
    }
  }, [selectedDeptHotelFilter, selectedMonth, selectedYear]);

  // Separate useEffect for Forecast data (depends on date range)
  useEffect(() => {
    if (token && hcode && hotelList.length > 0 && startDate && endDate) {
      fetchForecastActualData();
    }
  }, [selectedHotelFilter, startDate, endDate, activeTab]);

  // Fetch fallback orders when searching in Actual tab
  const fetchFallbackOrders = useCallback(() => {
    axios
      .post('/waiting/purchase/order', { hcode }, { headers })
      .then(function (response) {
        const data = Object.values(response.data || {}).flatMap(arr => arr) || [];
        setFallbackOrders(Array.isArray(data) ? data : []);
      })
      .catch(function () {
        setFallbackOrders([]);
      });
  }, [hcode, headers]);

  useEffect(() => {
    if (activeTab === 'actual' && (tableSearch || '').trim().length > 0 && token && hcode) {
      fetchFallbackOrders();
    }
  }, [activeTab, tableSearch, token, hcode]);

  // Separate useEffect for Spending Statistics (depends on month/year)
  useEffect(() => {
    if (token && hcode && hotelList.length > 0) {
      getSpendingStatistics();
    }
  }, [selectedHotelFilter, selectedMonth, selectedYear]);

  // Reset pagination when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Calculate totals
  const totalEarnings = countPOGenerate * 50000;
  const totalOrders = countPOGenerate + countPOSent;

  // Menu items moved to Layout component

  // Top ordered items data
  const [topOrderedItems, setTopOrderedItems] = useState([
    { name: 'Soap Happy', stock: 238, value: 19938 },
    { name: 'Shampoo', stock: 1038, value: 19938 },
    { name: 'Tissue', stock: 100, value: 1100, low: true },
    { name: 'Coffee', stock: 250, value: 2500 },
    { name: 'Towels', stock: 189, value: 1890 },
  ]);

  // Top spending departments data
  const [topSpendDepartments, setTopSpendDepartments] = useState([
    { name: 'Housekeeping', orders: 45, spent: 15000000, level: 'high' },
    { name: 'Kitchen', orders: 32, spent: 12000000, level: 'high' },
    { name: 'Front Office', orders: 28, spent: 8000000, level: 'medium' },
    { name: 'Engineering', orders: 22, spent: 6000000, level: 'medium' },
    { name: 'Security', orders: 15, spent: 3000000, level: 'low' },
  ]);

  // Spending statistics data for chart
  const [spendingStatistics, setSpendingStatistics] = useState({
    chartData: {
      labels: ['AS1', 'AS2', 'JU1'],
      datasets: [{
        data: [15000000, 12000000, 8000000],
        backgroundColor: ['rgba(147, 51, 234, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)']
      }]
    },
    summary: { total_spending: 35000000, total_hotels: 3, period: 'Sample Data' }
  });

  // Get session data for welcome message
  const sessionName = userData.name || 'User';

  const handleApplyFilter = () => {
    setSelectedHotelFilter(tempSelectedHotelFilter);
    setSelectedDepartmentFilter(tempSelectedDepartmentFilter);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  return (
    <Layout title="Dashboard" subtitle={`Welcome back, ${sessionName}`}>
      <div className="p-4 sm:p-6 md:p-8">


        {/* Loading Indicator */}
        {(loading || isLoading) && (
          <div className="bg-secondary-dark/10 border border-secondary-dark text-secondary-dark px-4 py-3 rounded mb-4">
            <p className="font-bold">🔄 Calculating . . .</p>
            <p className="text-sm">If this persists, check console for errors</p>
          </div>
        )}

        {/* Show dashboard content even if still loading some data */}
        {!token && (
          <div className="bg-accent-rose/10 border border-accent-rose text-accent-rose px-4 py-3 rounded mb-4">
            <p className="font-bold">⚠️ Authentication Error</p>
            <p>Please refresh the page or log in again</p>
          </div>
        )}

        {!hcode && token && (
          <div className="bg-accent-rose/10 border border-accent-rose text-accent-rose px-4 py-3 rounded mb-4">
            <p className="font-bold">⚠️ Session Error</p>
            <p>Hotel code missing from session. Please log in again.</p>
            <button
              onClick={() => {
                sessionStorage.clear();
                localStorage.clear();
                history.replace('/login');
              }}
              className="mt-2 px-4 py-2 bg-accent-rose text-white rounded hover:bg-secondary-dark"
            >
              Clear & Login Again
            </button>
            <div className="mt-2 text-xs">
              <p>Debug Info:</p>
              <p>_t exists: {sessionStorage.getItem('_t') ? 'Yes ✅' : 'No ❌'}</p>
              <p>_u exists: {sessionStorage.getItem('_u') ? 'Yes ✅' : 'No ❌'}</p>
              <p>User data: {JSON.stringify(userData)}</p>
            </div>
          </div>
        )}

        {/* Main Stats Cards - Matching Previous Dashboard */}
        {/* Stats Cards - Swiper on Mobile, Grid on Desktop */}
        <div className="mb-6 sm:mb-8">

          {/* Mobile Swiper  */}
          <div className="block md:hidden font-tasa">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={12}
              slidesPerView={2.2}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-2"
            >
              <SwiperSlide>
                <div
                  onClick={() => history.push('/waiting/proposal')}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden h-28"
                >
                  <div className="absolute -bottom-4 -right-4 opacity-5">
                    <FileText className="w-20 h-20 text-[#52357B] dark:text-[#648DB3]" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="bg-[#52357B]/10 w-8 h-8 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-[#52357B]" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-800 dark:text-white tracking-tight">{countPP}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Proposal</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div
                  onClick={() => history.push('/waiting/request')}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden h-28"
                >
                  <div className="absolute -bottom-4 -right-4 opacity-5">
                    <ClipboardList className="w-20 h-20 text-[#5459AC] dark:text-[#648DB3]" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="bg-[#5459AC]/10 w-8 h-8 rounded-lg flex items-center justify-center">
                      <ClipboardList className="w-4 h-4 text-[#5459AC]" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-800 dark:text-white tracking-tight">{countPR}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Request</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden h-28">
                  <div className="absolute -bottom-4 -right-4 opacity-5">
                    <ShoppingCart className="w-20 h-20 text-[#648DB3] dark:text-[#648DB3]" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="bg-[#648DB3]/10 w-8 h-8 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="w-4 h-4 text-[#648DB3]" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countPOGenerate}/{countPORecord}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">PO Gen</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden h-28">
                  <div className="absolute -bottom-4 -right-4 opacity-5">
                    <TruckIcon className="w-20 h-20 text-[#648DB3] dark:text-[#648DB3]" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="bg-[#648DB3]/10 w-8 h-8 rounded-lg flex items-center justify-center">
                      <TruckIcon className="w-4 h-4 text-[#648DB3]" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countPOSent}/{countPOGenerate}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">PO Sent</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div
                  onClick={() => history.push('/receive/item')}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden h-28"
                >
                  <div className="absolute -bottom-4 -right-4 opacity-5">
                    <CheckCircle className="w-20 h-20 text-[#5459AC] dark:text-[#648DB3]" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="bg-[#5459AC]/10 w-8 h-8 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-[#5459AC]" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countItemReceive}/{countPOSent}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Received</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>


              <SwiperSlide>
                <div
                  onClick={() => history.push('/items')}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden h-28"
                >
                  <div className="absolute -bottom-4 -right-4 opacity-5">
                    <Package className="w-20 h-20 text-[#52357B] dark:text-[#648DB3]" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div className="bg-[#52357B]/10 w-8 h-8 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-[#52357B]" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countItem}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">Items</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 font-tasa">
            {/* Purchase Proposal Card */}
            <div
              onClick={() => history.push('/waiting/proposal')}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer relative overflow-hidden group border border-gray-200 dark:border-gray-700"
            >
              <div className="absolute -bottom-6 -right-6 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                <FileText className="w-24 h-24 text-[#52357B] dark:text-[#648DB3]" />
              </div>
              <div className="relative z-10">
                <div className="bg-[#52357B]/10 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                  <FileText className="w-5 h-5 text-[#52357B]" />
                </div>
                <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countPP}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium tracking-wide uppercase">Proposal</p>
              </div>
            </div>

            {/* Purchase Request Card */}
            <div
              onClick={() => history.push('/waiting/request')}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer relative overflow-hidden group border border-gray-200 dark:border-gray-700"
            >
              <div className="absolute -bottom-6 -right-6 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                <ClipboardList className="w-24 h-24 text-[#5459AC] dark:text-[#648DB3]" />
              </div>
              <div className="relative z-10">
                <div className="bg-[#5459AC]/10 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                  <ClipboardList className="w-5 h-5 text-[#5459AC]" />
                </div>
                <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countPR}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium tracking-wide uppercase">Request</p>
              </div>
            </div>

            {/* PO Print Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all relative overflow-hidden group border border-gray-200 dark:border-gray-700">
              <div className="absolute -bottom-6 -right-6 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                <ShoppingCart className="w-24 h-24 text-[#648DB3] dark:text-[#648DB3]" />
              </div>
              <div className="relative z-10">
                <div className="bg-[#648DB3]/10 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                  <ShoppingCart className="w-5 h-5 text-[#648DB3]" />
                </div>
                <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countPOGenerate}/{countPORecord}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium tracking-wide uppercase">PO Generated</p>
              </div>
            </div>

            {/* PO Sent Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all relative overflow-hidden group border border-gray-200 dark:border-gray-700">
              <div className="absolute -bottom-6 -right-6 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                <TruckIcon className="w-24 h-24 text-[#648DB3] dark:text-[#648DB3]" />
              </div>
              <div className="relative z-10">
                <div className="bg-[#648DB3]/10 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                  <TruckIcon className="w-5 h-5 text-[#648DB3]" />
                </div>
                <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countPOSent}/{countPOGenerate}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium tracking-wide uppercase">PO Sent</p>
              </div>
            </div>

            {/* Items Receive Card */}
            <div
              onClick={() => history.push('/receive/item')}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer relative overflow-hidden group border border-gray-200 dark:border-gray-700"
            >
              <div className="absolute -bottom-6 -right-6 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                <CheckCircle className="w-24 h-24 text-[#5459AC] dark:text-[#648DB3]" />
              </div>
              <div className="relative z-10">
                <div className="bg-[#5459AC]/10 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                  <CheckCircle className="w-5 h-5 text-[#5459AC]" />
                </div>
                <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countItemReceive}/{countPOSent}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium tracking-wide uppercase">Received</p>
              </div>
            </div>

            {/* Total Items Card */}
            <div
              onClick={() => history.push('/items')}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all cursor-pointer relative overflow-hidden group border border-gray-200 dark:border-gray-700"
            >
              <div className="absolute -bottom-6 -right-6 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                <Package className="w-24 h-24 text-[#52357B] dark:text-[#648DB3]" />
              </div>
              <div className="relative z-10">
                <div className="bg-[#52357B]/10 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                  <Package className="w-5 h-5 text-[#52357B]" />
                </div>
                <p className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">{countItem}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium tracking-wide uppercase">Items</p>
              </div>
            </div>
          </div>
        </div>

        {/*Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8 font-tasa">

          {/* Total Orders Card */}
          <LoadingOverlay
            active={loadingForecastCards}
            spinner
            text="Calculating . . ."
          >
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 overflow-hidden group border border-gray-200 dark:border-gray-700">
            {/* Animated Background Motion */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#52357B]/5 to-[#5459AC]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            {/* Floating Animation Elements */}
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#52357B]/5 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/2 -left-12 w-32 h-32 bg-[#5459AC]/5 rounded-full blur-2xl animate-bounce" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>

            <div className="relative z-10 h-full flex flex-col justify-between">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const hotel = selectedHotelFilter ? hotelList.find(h => h.id_hotel == selectedHotelFilter) : null;
                      exportHotelTopItems(
                        hotel?.id_hotel,
                        hotel?.code_hotel || 'AHG',
                        hotel?.nm_hotel || 'All Hotels'
                      );
                    }}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-all border border-gray-200 dark:border-gray-600 shadow-sm"
                    title="Download Top 5 Items Excel"
                  >
                    <Download className="w-4 h-4 text-[#52357B] dark:text-white" />
                    <span className="text-sm font-bold text-gray-700 dark:text-white tracking-wide">
                      {selectedHotelFilter ?
                        hotelList.find(hotel => hotel.id_hotel == selectedHotelFilter)?.code_hotel || 'N/A'
                        : 'AHG'
                      }
                    </span>
                  </button>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">
                    Total Value
                  </span>
                </div>

                <div className="text-[10px] text-gray-500 dark:text-gray-300 font-medium bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {startDate && endDate ? (
                    `${Moment(startDate).format('D MMM')} to ${Moment(endDate).format('D MMM YYYY')}`
                  ) : (
                    'Loading...'
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Total Orders</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white tracking-tight">
                  IDR {forecastActualData
                    .reduce((total, item) => {
                      const amount = item.quotation_total ? parseInt(item.quotation_total) : 0;
                      return total + amount;
                    }, 0)
                    .toLocaleString('id-ID')
                    .replace(/,/g, '.')
                  }
                </p>
                <div className="flex items-center space-x-2 mt-2">
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">All Orders Value (Forecast + Actual)</p>
                </div>
              </div>
            </div>
          </div>
          </LoadingOverlay>

          {/* Forecast IDR Total Card */}
          <LoadingOverlay
            active={loadingForecastCards}
            spinner
            text="Calculating . . ."
          >
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 overflow-hidden group border border-gray-200 dark:border-gray-700">
            {/* Animated Background Motion */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#5459AC]/5 to-[#648DB3]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            {/* Floating Animation Elements */}
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#5459AC]/5 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#648DB3]/5 rounded-full blur-2xl" style={{ animationDelay: '0.5s' }}></div>

            <div className="relative z-10 h-full flex flex-col justify-between">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                <div className="flex items-center space-x-3">
                  <button
                    className="flex items-center space-x-2 px-3 py-1.5 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-all border border-gray-200 dark:border-gray-600 shadow-sm"
                  >
                    <Download className="w-4 h-4 text-[#5459AC] dark:text-white" />
                    <span className="text-sm font-bold text-gray-700 dark:text-white tracking-wide">AHG</span>
                  </button>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">
                    Forecast Value
                  </span>
                </div>

                <div className="text-[10px] text-gray-500 dark:text-gray-300 font-medium bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {startDate && endDate
                    ? `${Moment(startDate).format('D MMM')} to ${Moment(endDate).format('D MMM YYYY')}`
                    : 'Loading...'}
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Forecast Total</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white tracking-tight">
                  IDR {forecastActualData
                    .filter(item => !item.receive_status || parseInt(item.receive_status) !== 1)
                    .reduce((total, item) => {
                      const amount = item.quotation_total ? parseInt(item.quotation_total) : 0;
                      return total + amount;
                    }, 0)
                    .toLocaleString('id-ID')
                    .replace(/,/g, '.')
                  }
                </p>
                <div className="flex items-center space-x-2 mt-2">
                  <span className="flex h-2 w-2 relative">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400"></span>
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Pending Orders Value</p>
                </div>
              </div>
            </div>
          </div>
          </LoadingOverlay>

          {/* Actual IDR Total Card */}
          <LoadingOverlay
            active={loadingForecastCards}
            spinner
            text="Calculating . . ."
          >
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 overflow-hidden group border border-gray-200 dark:border-gray-700">
            {/* Animated Background Motion */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#648DB3]/5 to-[#52357B]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            {/* Floating Animation Elements */}
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#648DB3]/5 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-10 right-10 w-20 h-20 bg-[#52357B]/5 rounded-full blur-xl animate-spin-slow"></div>

            <div className="relative z-10 h-full flex flex-col justify-between">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                <div className="flex items-center space-x-3">
                  <button
                    className="flex items-center space-x-2 px-3 py-1.5 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-all border border-gray-200 dark:border-gray-600 shadow-sm"
                  >
                    <Download className="w-4 h-4 text-[#648DB3] dark:text-white" />
                    <span className="text-sm font-bold text-gray-700 dark:text-white tracking-wide">AHG</span>
                  </button>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">
                    Actual Value
                  </span>
                </div>

                <div className="text-[10px] text-gray-500 dark:text-gray-300 font-medium bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {startDate && endDate
                    ? `${Moment(startDate).format('D MMM')} to ${Moment(endDate).format('D MMM YYYY')}`
                    : 'Loading...'}
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Actual Total</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white tracking-tight">
                  IDR {forecastActualData
                    .filter(item => item.receive_status && parseInt(item.receive_status) === 1)
                    .reduce((total, item) => {
                      const amount = item.quotation_total ? parseInt(item.quotation_total) : 0;
                      return total + amount;
                    }, 0)
                    .toLocaleString('id-ID')
                    .replace(/,/g, '.')
                  }
                </p>
                <div className="flex items-center space-x-2 mt-2">
                  <span className="flex h-2 w-2 relative">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400"></span>
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Received Orders Value</p>
                </div>
              </div>
            </div>
          </div>
          </LoadingOverlay>

        </div>

        {/* Forecast & Actual Receive */}
        <div className=" dark:bg-gray-800 rounded-2xl p-6 shadow-xl mb-8 border border-light-gray dark:border-gray-700">
          <div className="mb-6 dark:text-gray-100">
            {/* Desktop: Title left, Tabs+Button right | Mobile: Stacked */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-2">
              {/* Title */}
              <h3 className="text-lg font-bold text-text-dark dark:text-gray-100">Forecast & Actual Receive</h3>

              {/* Tab Buttons + Export Button (grouped right on desktop) */}
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Tab Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setActiveTab('forecast')}
                    className={`px-4 py-2 text-sm font-semibold dark:text-gray-100 rounded-lg transition-colors ${activeTab === 'forecast'
                      ? 'bg-accent-rose dark:text-gray-100 text-white'
                      : 'text-secondary-dark dark:text-gray-100 hover:bg-light-gray'
                      }`}
                  >
                    Forecast
                  </button>
                  <button
                    onClick={() => setActiveTab('actual')}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeTab === 'actual'
                      ? 'bg-accent-rose dark:text-gray-50 text-white'
                      : 'text-secondary-dark dark:text-gray-100 hover:bg-light-gray dark:hover:bg-gray-200'
                      }`}
                  >
                    Actual Receive
                  </button>
                </div>

                {/* Export Button */}
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center justify-center space-x-2"
                  title="Export"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              </div>
            </div>

            {/* Explanatory Text */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-700">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-secondary-dark dark:text-gray-100">Range:</span>
                <span className="bg-gray-100 dark:bg-gray-700 text-secondary-dark dark:text-gray-200 px-2 py-0.5 rounded-full">
                  {startDate && endDate ? (
                    `${Moment(startDate).format('D MMM YYYY')} - ${Moment(endDate).format('D MMM YYYY')}`
                  ) : (
                    'Loading...'
                  )}
                </span>
              </div>
              <span className="text-gray-300 dark:text-gray-500">|</span>
              <div className="flex items-center space-x-1">
                <span className="font-semibold text-secondary-dark dark:text-gray-100">Forecast:</span>
                <span className="italic dark:text-gray-100">PO sent to supplier but not received yet</span>
              </div>
              <span className="text-gray-300 dark:text-gray-500">|</span>
              <div className="flex items-center space-x-1">
                <span className="font-semibold text-accent-rose dark:text-gray-100">Actual:</span>
                <span className="italic dark:text-gray-100">Items already received by user</span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-light-gray dark:border-gray-700 items-end">
            <div className="flex flex-col space-y-1 w-full">
              <label className="text-sm font-semibold text-secondary-dark dark:text-gray-300 whitespace-nowrap">Hotel:</label>
              <select
                value={tempSelectedHotelFilter}
                onChange={(e) => {
                  setTempSelectedHotelFilter(e.target.value);
                }}
                className="w-full px-3 py-2 text-sm border border-light-gray dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-900 dark:bg-gray-100 dark:bg-opacity-10 font-medium bg-transparent"
              >
                <option value="" className="dark:text-gray-900">All Hotels</option>
                {hotelList.length > 0 ? (
                  hotelList.map((hotel) => (
                    <option key={hotel.id_hotel} value={hotel.id_hotel} className="dark:text-gray-900">
                      {hotel.nm_hotel}
                    </option>
                  ))
                ) : (
                  <option disabled className="dark:text-gray-900">Loading hotels...</option>
                )}
              </select>
            </div>
            <div className="flex flex-col space-y-1 w-full">
              <label className="text-sm font-semibold text-secondary-dark dark:text-gray-300 whitespace-nowrap">Department:</label>
              <select
                value={tempSelectedDepartmentFilter}
                onChange={(e) => setTempSelectedDepartmentFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-light-gray dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-900 dark:bg-gray-100 dark:bg-opacity-10 font-medium bg-transparent"
              >
                <option value="" className="dark:text-gray-900">All Departments</option>
                {Array.from(
                  new Set(
                    forecastActualData
                      .map(item => item.nm_department)
                      .filter(Boolean)
                  )
                ).map((deptName, index) => (
                  <option key={deptName || index} value={deptName} className="dark:text-gray-900">
                    {deptName}
                  </option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div className="flex flex-col space-y-1 w-full">
              <label className="text-sm font-semibold text-secondary-dark dark:text-gray-300 whitespace-nowrap">Start:</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-light-gray dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-900 dark:bg-gray-100 dark:bg-opacity-10 font-medium bg-transparent"
              />
            </div>

            {/* End Date */}
            <div className="flex flex-col space-y-1 w-full">
              <label className="text-sm font-semibold text-secondary-dark dark:text-gray-300 whitespace-nowrap">End:</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-light-gray dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-900 dark:bg-gray-100 dark:bg-opacity-10 font-medium bg-transparent"
              />
            </div>

            {/* Apply Button */}
            <div className="flex items-end w-full">
              <button
                onClick={handleApplyFilter}
                className="w-full px-4 py-2 text-sm font-semibold rounded-lg bg-accent-rose text-white hover:bg-secondary-dark transition-colors shadow-md flex items-center justify-center space-x-2"
              >
                <Filter className="w-4 h-4" />
                <span>Apply Filter</span>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-300">
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700">PO No.</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700">Item</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700">Qty / Unit</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700">Supplier</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700 w-32">Est. Rec</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700 w-32">Need</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700 w-32">PP Created</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700 w-24">PO Generated</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700 w-24">Date Receive</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700">Price</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700">Total</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700 w-64">Note</th>
                  <th className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-700">Status</th>
                  <th className="text-center py-1 px-2 font-medium text-gray-600 dark:text-gray-700">Action</th>
                </tr>
                <tr>
                  <td colSpan="14" className="px-2 py-2">
                    <input
                      type="text"
                      value={tableSearch}
                      onChange={e => {
                        setTableSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search all columns..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs"
                    />
                  </td>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const baseData = activeTab === 'actual'
                    ? [...forecastActualData, ...fallbackOrders]
                    : forecastActualData;
                  // Deduplicate by (id_pp, nomer, nama_item)
                  const seen = new Set();
                  const uniqueData = baseData.filter(it => {
                    const key = `${it.id_pp || ''}|${it.nomer || ''}|${it.nama_item || ''}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });
                  const filtered = uniqueData.filter(item => {
                    const matchesTab = activeTab === 'forecast'
                      ? item.receive_status == null
                      : item.receive_status != null;
                    const matchesDate = activeTab === 'actual'
                      ? isWithinRange(normalizeReceiveDate(item), startDate, endDate)
                      : isWithinRange(item.created_at || item.pp_created_at, startDate, endDate);
                    const matchesDepartment =
                      !selectedDepartmentFilter || item.nm_department === selectedDepartmentFilter || !item.nm_department;
                    const matchesQ = matchSearch(item, tableSearch);
                    return matchesTab && matchesDepartment && matchesDate && matchesQ;
                  });
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  return filtered.slice(startIndex, endIndex);
                })()
                  .map((item, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-50">
                      {/* PO No */}
                      <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-900">
                        <div className="truncate" title={item.nomer}>{item.nomer}</div>
                        <div className="text-xs text-gray-500 font-normal truncate" title={item.nm_department}>{item.nm_department || '-'}</div>
                        <div className="text-xs text-gray-400 font-normal italic truncate" title={item.nm_hotel}>{item.nm_hotel || '-'}</div>
                      </td>

                      {/* Item */}
                      <td className="py-2 px-2">
                        <div>
                          <div className="text-xs text-gray-500 truncate">
                            {item.quotation_total && (
                              <span className="text-accent-rose">
                                (IDR {parseInt(item.quotation_price).toLocaleString('id-ID')})
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-600 line-clamp-2" title={item.nama_item}>{item.nama_item}</div>
                        </div>
                      </td>

                      {/* Qty / Unit */}
                      <td className="py-2 px-2 text-gray-600 dark:text-gray-700">
                        {(() => {
                          let quantity = '0';
                          let unit = '';

                          // Determine Quantity
                          if (item.unit && typeof item.unit === 'string') {
                            quantity = item.quantity ? item.quantity.toString().trim() : '0';
                          } else if (item.quantity) {
                            const quantityStr = item.quantity.toString().trim();
                            const match = quantityStr.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
                            if (match) {
                              quantity = match[1];
                            } else {
                              quantity = quantityStr;
                            }
                          }

                          // Determine Unit
                          if (item.unit && typeof item.unit === 'string') {
                            unit = item.unit.trim();
                          } else if (item.quantity) {
                            const quantityStr = item.quantity.toString().trim();
                            const match = quantityStr.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
                            if (match) {
                              unit = match[2];
                            }
                          }

                          return (
                            <div>
                              <span className="font-semibold">{quantity}</span>
                              <span className="text-gray-500 ml-1">{unit || '-'}</span>
                            </div>
                          );
                        })()}
                      </td>

                      {/* Supplier */}
                      <td className="py-2 px-2 text-gray-600 dark:text-gray-700">
                        <div className="line-clamp-3" title={item.companyname}>
                          {item.companyname || '-'}
                        </div>
                      </td>

                      {/* Est. Receive */}
                      <td className="py-2 px-2">
                        {item.item_must_receive && Moment(item.item_must_receive).format('DD MMM YYYY')}
                      </td>

                      {/* Need Date */}
                      <td className="py-2 px-2">
                        {item.date_need && Moment(item.date_need).format('DD MMM YYYY')}
                      </td>

                      {/* PO Created */}
                      <td className="py-2 px-2">
                        {item.pp_created_at && Moment(item.pp_created_at).format('DD MMM YYYY')}
                      </td>

                      {/* PO Generated */}
                      <td className="py-2 px-2">
                        {item.created_at && Moment(item.created_at).format('DD MMM YYYY')}
                      </td>
                      
                      {/* Date Receive */}
                      <td className="py-2 px-2">
                        {activeTab === 'actual' ? (() => {
                          const d = item.receive_item_date || item.receive_date || item.date_receive;
                          return d ? Moment(d).format('DD MMM YYYY') : '-';
                        })() : '-'}
                      </td>

                      {/* Price */}
                      <td className="py-2 px-2 text-right">
                        {item.quotation_price ? parseInt(item.quotation_price).toLocaleString('id-ID') : '0'}
                      </td>

                      {/* Total */}
                      <td className="py-2 px-2 text-right">
                        {item.quotation_total ? parseInt(item.quotation_total).toLocaleString('id-ID') : '0'}
                      </td>

                      {/* Remarks */}
                      <td className="py-2 px-2 text-gray-600 dark:text-gray-700">
                        <div className="line-clamp-3" title={item.head}>
                          {item.head || '-'}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-2 px-2">
                        {(() => {
                          switch (parseInt(item.receive_status)) {
                            case 1:
                              return <span className="px-2 py-1 text-[10px] font-medium rounded-full bg-accent-rose/20 text-accent-rose">Full Receive</span>;
                            case 2:
                              return <span className="px-2 py-1 text-[10px] font-medium rounded-full bg-secondary-dark/20 text-secondary-dark">Half Receive</span>;
                            case 3:
                              return <span className="px-2 py-1 text-[10px] font-medium rounded-full bg-accent-rose/30 text-accent-rose">Defect Receive</span>;
                            case 4:
                              return <span className="px-2 py-1 text-[10px] font-medium rounded-full bg-gray-100 text-gray-800">Reject</span>;
                            default:
                              return <span className="px-2 py-1 text-[10px] font-medium rounded-full bg-gray-100 text-gray-500">Waiting for Received</span>;
                          }
                        })()}
                      </td>

                      {/* Action */}
                      <td className="py-2 px-2 text-center">
                        <button
                          onClick={() => history.push(`/detail/proposal/${item.id_pp}`)}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-blue-600"
                          title="View Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}

                {/* Total Row */}
                {forecastActualData.length > 0 && (
                  <>
                    <tr className="border-t-2 border-gray-300 dark:border-gray-400 bg-gray-50 dark:bg-gray-50 font-semibold">
                      <td className="py-3 px-2 font-[10px] text-gray-800 dark:text-gray-900 text-right" colSpan="10">
                        TOTAL
                      </td>
                      <td className="py-3 px-2 text-right whitespace-nowrap">
                        <div className="text-xs font-bold text-red-600">
                          IDR {(() => {
                             const baseData = activeTab === 'actual'
                               ? [...forecastActualData, ...fallbackOrders]
                               : forecastActualData;
                             const seen = new Set();
                             const uniqueData = baseData.filter(it => {
                               const key = `${it.id_pp || ''}|${it.nomer || ''}|${it.nama_item || ''}`;
                               if (seen.has(key)) return false;
                               seen.add(key);
                               return true;
                             });
                             const filtered = uniqueData.filter(item => {
                              const matchesTab = activeTab === 'forecast'
                                ? item.receive_status == null
                                : item.receive_status != null;
                               const matchesDate = activeTab === 'actual'
                                ? isWithinRange(normalizeReceiveDate(item), startDate, endDate)
                                : isWithinRange(item.created_at || item.pp_created_at, startDate, endDate);
                              const matchesDepartment =
                                 !selectedDepartmentFilter || item.nm_department === selectedDepartmentFilter || !item.nm_department;
                              const matchesQ = matchSearch(item, tableSearch);
                              return matchesTab && matchesDepartment && matchesDate && matchesQ;
                            });
                            const total = filtered.reduce((total, item) => {
                              const amount = item.quotation_total ? parseInt(item.quotation_total) : 0;
                              return total + amount;
                            }, 0);
                            return total.toLocaleString('id-ID');
                          })()}
                        </div>
                      </td>
                      <td className="py-3 px-2" colSpan="3"></td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            {forecastActualData.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No data available
              </div>
            )}

            {/* Pagination Controls */}
            {forecastActualData.length > 0 && (() => {
              const baseData = activeTab === 'actual'
                ? [...forecastActualData, ...fallbackOrders]
                : forecastActualData;
              const seen = new Set();
              const uniqueData = baseData.filter(it => {
                const key = `${it.id_pp || ''}|${it.nomer || ''}|${it.nama_item || ''}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
              const filtered = uniqueData.filter(item => {
                const matchesTab = activeTab === 'forecast'
                  ? item.receive_status == null
                  : item.receive_status != null;
                const matchesDate = activeTab === 'actual'
                  ? isWithinRange(normalizeReceiveDate(item), startDate, endDate)
                  : true;
                const matchesDepartment =
                  !selectedDepartmentFilter || item.nm_department === selectedDepartmentFilter || !item.nm_department;
                const matchesQ = matchSearch(item, tableSearch);
                return matchesTab && matchesDepartment && matchesDate && matchesQ;
              });
              const totalPages = Math.ceil(filtered.length / itemsPerPage);

              if (totalPages <= 1) return null;

              return (
                <div className="flex items-center justify-between px-4 py-4 border-t border-gray-200 dark:border-gray-300">
                  <div className="text-sm text-gray-600 dark:text-gray-800">
                    Showing <span className="font-semibold">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                    <span className="font-semibold">{Math.min(currentPage * itemsPerPage, filtered.length)}</span> of{' '}
                    <span className="font-semibold">{filtered.length}</span> items
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => {
                        setCurrentPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${currentPage === 1
                        ? 'bg-gray-100 dark:bg-gray-200 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-white dark:bg-white text-gray-700 dark:text-gray-800 hover:bg-purple-50 dark:hover:bg-gray-50 hover:text-purple-700 border border-gray-300 dark:border-gray-300'
                        }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        // Show first page, last page, current page, and pages around current
                        const shouldShow =
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1);

                        if (!shouldShow) {
                          // Show ellipsis
                          if (page === currentPage - 2 || page === currentPage + 2) {
                            return <span key={page} className="px-2 text-gray-400">...</span>;
                          }
                          return null;
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${currentPage === page
                              ? 'bg-purple-600 text-white'
                              : 'bg-white dark:bg-white text-gray-700 dark:text-gray-800 hover:bg-purple-50 dark:hover:bg-gray-50 hover:text-purple-700 border border-gray-300 dark:border-gray-300'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => {
                        setCurrentPage(prev => Math.min(totalPages, prev + 1));
                      }}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${currentPage === totalPages
                        ? 'bg-gray-100 dark:bg-gray-200 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-white dark:bg-white text-gray-700 dark:text-gray-800 hover:bg-purple-50 dark:hover:bg-gray-50 hover:text-purple-700 border border-gray-300 dark:border-gray-300'
                        }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Department Spending */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-light-gray dark:border-gray-700">
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-text-dark dark:text-white">Department Spending</h3>
                <button
                  onClick={exportDepartmentSpending}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center space-x-1"
                  title="Export"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              </div>
              <p className="text-xs text-secondary-dark dark:text-gray-400 mt-1 italic">All orders (forecast + actual)</p>
            </div>

            {/* Hotel, Month/Year Selection */}
            <div className="filter-scroll-mobile items-center gap-3 mb-6 p-3 bg-white dark:bg-gray-700 rounded-lg shadow-md border border-light-gray dark:border-gray-600">
              {/* Hotel Selection */}
              <div className="filter-item-nowrap flex items-center space-x-2">
                <label className="text-xs font-semibold text-secondary-dark dark:text-gray-200 whitespace-nowrap">Hotel:</label>
                <select
                  value={selectedDeptHotelFilter}
                  onChange={(e) => setSelectedDeptHotelFilter(e.target.value)}
                  className="px-2 py-1 text-xs border border-light-gray dark:border-gray-600 rounded focus:ring-1 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-200 dark:bg-gray-600 font-medium"
                >
                  <option value="">All Hotels</option>
                  {hotelList.length > 0 ? (
                    hotelList.map((hotel) => (
                      <option key={hotel.id_hotel} value={hotel.id_hotel}>
                        {hotel.code_hotel || hotel.nm_hotel}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading...</option>
                  )}
                </select>
              </div>

              {/* Month Selection */}
              <div className="filter-item-nowrap flex items-center space-x-2">
                <label className="text-xs font-semibold text-secondary-dark whitespace-nowrap">Month:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="px-2 py-1 text-xs border border-light-gray rounded focus:ring-1 focus:ring-accent-rose focus:border-accent-rose text-primary-dark font-medium"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('en', { month: 'short' })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Selection */}
              <div className="filter-item-nowrap flex items-center space-x-2">
                <label className="text-xs font-semibold text-secondary-dark whitespace-nowrap">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-2 py-1 text-xs border border-light-gray rounded focus:ring-1 focus:ring-accent-rose focus:border-accent-rose text-primary-dark font-medium"
                >
                  {Array.from({ length: 3 }, (_, i) => {
                    const year = new Date().getFullYear() - 1 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Current Selection Display */}
              <div className="filter-item-nowrap text-xs text-secondary-dark flex items-center space-x-1">
                <span className="font-semibold whitespace-nowrap">Current:</span>
                <span className="px-2 py-1 bg-accent-rose/10 text-accent-rose font-medium rounded border border-accent-rose whitespace-nowrap">
                  {selectedDeptHotelFilter ?
                    hotelList.find(hotel => hotel.id_hotel == selectedDeptHotelFilter)?.code_hotel || 'Hotel'
                    : 'All'
                  } | {new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'short' })} {selectedYear}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              {topSpendDepartments.map((dept, index) => (
                <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <div className="flex items-center space-x-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${dept.level === 'high' ? 'bg-red-100' :
                      dept.level === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                      }`}>
                      <span className="text-sm text-white">
                        {dept.level === 'high' ? '🔥' : dept.level === 'medium' ? '⚡' : '💚'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-primary-dark dark:text-gray-200">{dept.name}</p>
                      <p className="text-xs text-secondary-dark dark:text-gray-400">
                        {dept.orders} orders
                        {dept.hotel && <span className="text-accent-rose ml-1">| {dept.hotel}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-bold ${dept.level === 'high' ? 'text-red-600' :
                      dept.level === 'medium' ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                      IDR {dept.spent.toLocaleString('id-ID')}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {dept.level === 'high' ? 'High Spender' :
                        dept.level === 'medium' ? 'Medium Spender' : 'Low Spender'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Most Ordered Items */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-light-gray dark:border-gray-700">
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-text-dark dark:text-white">Top Spend Items</h3>
                <button
                  onClick={exportTopSpendItems}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center space-x-1"
                  title="Export to Excel"
                >
                  <Download className="w-4 h-4" />
                  <span>Export Excel</span>
                </button>
              </div>
              <p className="text-xs text-secondary-dark dark:text-gray-400 mt-1 italic">Based on received items (sorted by total spending)</p>
            </div>

            {/* Hotel, Month/Year Selection */}
            <div className="filter-scroll-mobile items-center gap-3 mb-6 p-3 bg-white dark:bg-gray-700 rounded-lg shadow-md border border-light-gray dark:border-gray-600">
              {/* Hotel Selection */}
              <div className="filter-item-nowrap flex items-center space-x-2">
                <label className="text-xs font-semibold text-secondary-dark dark:text-gray-200 whitespace-nowrap">Hotel:</label>
                <select
                  value={selectedHotelFilter}
                  onChange={(e) => setSelectedHotelFilter(e.target.value)}
                  className="px-2 py-1 text-xs border border-light-gray dark:border-gray-600 rounded focus:ring-1 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-200 dark:bg-gray-600 font-medium"
                >
                  <option value="">All Hotels</option>
                  {hotelList.length > 0 ? (
                    hotelList.map((hotel) => (
                      <option key={hotel.id_hotel} value={hotel.id_hotel}>
                        {hotel.code_hotel || hotel.nm_hotel}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading...</option>
                  )}
                </select>
              </div>

              {/* Month Selection */}
              <div className="filter-item-nowrap flex items-center space-x-2">
                <label className="text-xs font-semibold text-secondary-dark whitespace-nowrap">Month:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="px-2 py-1 text-xs border border-light-gray rounded focus:ring-1 focus:ring-accent-rose focus:border-accent-rose text-primary-dark font-medium"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('en', { month: 'short' })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Selection */}
              <div className="filter-item-nowrap flex items-center space-x-2">
                <label className="text-xs font-semibold text-secondary-dark whitespace-nowrap">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-2 py-1 text-xs border border-light-gray rounded focus:ring-1 focus:ring-accent-rose focus:border-accent-rose text-primary-dark font-medium"
                >
                  {Array.from({ length: 3 }, (_, i) => {
                    const year = new Date().getFullYear() - 1 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Current Selection Display */}
              <div className="filter-item-nowrap text-xs text-secondary-dark flex items-center space-x-1">
                <span className="font-semibold whitespace-nowrap">Current:</span>
                <span className="px-2 py-1 bg-accent-rose/10 text-accent-rose font-medium rounded border border-accent-rose whitespace-nowrap">
                  {selectedHotelFilter ?
                    hotelList.find(hotel => hotel.id_hotel == selectedHotelFilter)?.code_hotel || 'Hotel'
                    : 'All'
                  } | {new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'short' })} {selectedYear}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {topOrderedItems.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer group"
                  onClick={() => item.latest_pp_id && history.push(`/detail/proposal/${item.latest_pp_id}`)}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden group-hover:ring-2 group-hover:ring-purple-100 transition-all">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<span class="text-sm">📦</span>';
                          }}
                        />
                      ) : (
                        <span className="text-sm">📦</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 line-clamp-1 group-hover:text-purple-600 transition-colors">{item.name}</p>
                      <div className="flex items-center space-x-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.stock} {item.unit || 'units'}</p>
                        <span className="text-gray-300">•</span>
                        <p className="text-xs text-blue-600 font-medium">
                          IDR {item.avgPrice ? item.avgPrice.toLocaleString('id-ID') : '0'}
                        </p>
                      </div>
                      {item.hotel && (
                        <p className="text-[10px] text-gray-400 mt-0.5">{item.hotel}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-purple-600 whitespace-nowrap group-hover:scale-105 transition-transform">
                      IDR {item.value.toLocaleString('id-ID')}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {item.orderCount ? `${item.orderCount} orders` : 'Total'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Statistics Section - Full Width Cards (1 row each) */}
        <div className="mt-8 space-y-8"></div>
        {/* Total Spending Statistics - Hotel Based */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-light-gray dark:border-gray-700">
          {/* Header Section */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-text-dark dark:text-white mb-2">Hotel Spending</h3>
            <p className="text-2xl font-bold text-secondary-dark">
              IDR {spendingStatistics.summary.total_spending ? spendingStatistics.summary.total_spending.toLocaleString('id-ID') : '0'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {spendingStatistics.summary.period || 'Current Period'} | {spendingStatistics.summary.total_hotels || 0} Hotels
            </p>
          </div>

          {/* Month/Year Selection */}
          <div className="filter-scroll-mobile items-center gap-3 mb-6 p-3 bg-white dark:bg-gray-700 rounded-lg shadow-md border border-light-gray dark:border-gray-600">
            <div className="filter-item-nowrap flex items-center space-x-2">
              <label className="text-xs font-semibold text-secondary-dark dark:text-gray-200 whitespace-nowrap">Month:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-2 py-1 text-xs border border-light-gray dark:border-gray-600 rounded focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-200 dark:bg-gray-600 font-medium"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en', { month: 'short' })}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-item-nowrap flex items-center space-x-2">
              <label className="text-xs font-semibold text-secondary-dark whitespace-nowrap">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-2 py-1 text-xs border border-light-gray dark:border-gray-600 rounded focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-200 dark:bg-gray-600 font-medium"
              >
                {Array.from({ length: 3 }, (_, i) => {
                  const year = new Date().getFullYear() - 1 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Hotel Spending Line Chart */}
          <div className="h-[350px] sm:h-[500px] md:h-[600px] lg:h-[700px] bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-4 overflow-x-auto">
            {spendingStatistics.chartData.labels && spendingStatistics.chartData.labels.length > 0 ? (
              <div className="w-full h-full min-w-[600px]">
                <svg className="w-full h-full" viewBox="0 0 1500 700" preserveAspectRatio="xMinYMid meet">
                  {/* Clean Background */}
                  <rect width="1500" height="700" fill="white" />

                  {/* Chart Area Background */}
                  <rect x="120" y="80" width="1200" height="480" fill="#fafafa" stroke="#e5e7eb" strokeWidth="1" />

                  {/* Grid Lines */}
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <line
                      key={`grid-${i}`}
                      x1="120"
                      y1={80 + (i * 80)}
                      x2="1320"
                      y2={80 + (i * 80)}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="4,4"
                    />
                  ))}

                  {/* Vertical Grid Lines */}
                  {spendingStatistics.chartData.labels.map((_, i) => {
                    const labelCount = spendingStatistics.chartData.labels.length;
                    const x = labelCount > 1 ? 120 + ((1200 / (labelCount - 1)) * i) : 120;
                    return (
                      <line
                        key={`vgrid-${i}`}
                        x1={x || 120}
                        y1="80"
                        x2={x || 120}
                        y2="560"
                        stroke="#f3f4f6"
                        strokeWidth="1"
                      />
                    );
                  })}

                  {/* Y-Axis */}
                  <line x1="120" y1="80" x2="120" y2="560" stroke="#374151" strokeWidth="3" />

                  {/* X-Axis */}
                  <line x1="120" y1="560" x2="1320" y2="560" stroke="#374151" strokeWidth="3" />

                  {/* Y-Axis Labels */}
                  {(() => {
                    const allData = spendingStatistics.chartData.datasets?.[0]?.data || [];
                    const maxSpending = allData.length > 0 ? Math.max(...allData.filter(d => !isNaN(d) && d != null)) : 1;
                    const validMaxSpending = isNaN(maxSpending) || maxSpending <= 0 ? 1 : maxSpending;
                    return [0, 1, 2, 3, 4, 5, 6].map(i => {
                      const value = (validMaxSpending / 6) * (6 - i);
                      const label = value > 1000000 ? `${(value / 1000000).toFixed(1)}M` :
                        value > 1000 ? `${(value / 1000).toFixed(0)}K` :
                          value.toFixed(0);
                      return (
                        <text
                          key={`y-label-${i}`}
                          x="110"
                          y={90 + (i * 80)}
                          textAnchor="end"
                          fontSize="14"
                          fill="#6b7280"
                          fontFamily="system-ui"
                          fontWeight="500"
                        >
                          {label}
                        </text>
                      );
                    });
                  })()}

                  {/* Data Line and Points */}
                  {(() => {
                    const labels = spendingStatistics.chartData.labels || [];
                    const data = spendingStatistics.chartData.datasets?.[0]?.data || [];
                    const maxSpending = data.length > 0 ? Math.max(...data.filter(d => !isNaN(d) && d != null)) : 1;

                    // Validasi untuk mencegah NaN
                    if (!labels.length || labels.length === 0 || maxSpending <= 0) {
                      return null;
                    }

                    const labelCount = labels.length;
                    const xStep = labelCount > 1 ? 1200 / (labelCount - 1) : 0;

                    const points = labels.map((hotel, i) => {
                      const spending = data[i] || 0;
                      const validSpending = isNaN(spending) || spending == null ? 0 : Number(spending);
                      const x = labelCount > 1 ? 120 + (xStep * i) : 720; // center jika hanya 1 label
                      const y = maxSpending > 0 ? 560 - ((validSpending / maxSpending) * 480) : 560;
                      // Pastikan x dan y adalah angka valid
                      return {
                        x: isNaN(x) ? 120 : x,
                        y: isNaN(y) ? 560 : y,
                        hotel,
                        spending: validSpending
                      };
                    });

                    return (
                      <g>
                        {/* Gradient Definition */}
                        <defs>
                          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#3b82f6" />
                          </linearGradient>
                          <filter id="shadow">
                            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.1" />
                          </filter>
                        </defs>

                        {/* Area under line */}
                        {points.length > 1 && (
                          <path
                            d={`M 120,560 L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${points[points.length - 1].x},560 Z`}
                            fill="url(#lineGradient)"
                            fillOpacity="0.1"
                          />
                        )}

                        {/* Main Line */}
                        {points.length > 1 && (
                          <path
                            d={`M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`}
                            fill="none"
                            stroke="url(#lineGradient)"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            filter="url(#shadow)"
                          />
                        )}

                        {/* Data Points */}
                        {points.map((point, i) => (
                          <g
                            key={`point-${i}`}
                            onMouseEnter={() => setHoveredPoint({ type: 'hotel', ...point })}
                            onMouseLeave={() => setHoveredPoint(null)}
                            className="cursor-pointer"
                          >
                            {/* Larger Invisible Area for Easier Hover */}
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r="20"
                              fill="transparent"
                            />
                            <circle
                              cx={point.x + 2}
                              cy={point.y + 2}
                              r={hoveredPoint?.hotel === point.hotel ? "10" : "8"}
                              fill="#000"
                              fillOpacity="0.1"
                            />

                            {/* Point Circle */}
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r={hoveredPoint?.hotel === point.hotel ? "10" : "8"}
                              fill="white"
                              stroke={hoveredPoint?.hotel === point.hotel ? "#6366f1" : "#8b5cf6"}
                              strokeWidth="4"
                              className="transition-all duration-200"
                            />

                            {/* Inner Point */}
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r="4"
                              fill={hoveredPoint?.hotel === point.hotel ? "#6366f1" : "#8b5cf6"}
                            />

                            {/* X-Axis Labels */}
                            <text
                              x={point.x}
                              y="600"
                              textAnchor="middle"
                              fontSize="14"
                              fill={hoveredPoint?.hotel === point.hotel ? "#6366f1" : "#374151"}
                              fontWeight={hoveredPoint?.hotel === point.hotel ? "700" : "600"}
                              fontFamily="system-ui"
                              className="transition-all"
                            >
                              {point.hotel}
                            </text>

                            {/* Tooltip on Hover */}
                            {hoveredPoint?.hotel === point.hotel && (
                              <g>
                                {/* Tooltip Background */}
                                <rect
                                  x={point.x - 80}
                                  y={point.y - 60}
                                  width="160"
                                  height="50"
                                  fill="rgba(0, 0, 0, 0.85)"
                                  rx="8"
                                />
                                <text
                                  x={point.x}
                                  y={point.y - 35}
                                  textAnchor="middle"
                                  fontSize="12"
                                  fill="white"
                                  fontWeight="600"
                                  fontFamily="system-ui"
                                >
                                  {point.hotel}
                                </text>
                                <text
                                  x={point.x}
                                  y={point.y - 18}
                                  textAnchor="middle"
                                  fontSize="14"
                                  fill="#a78bfa"
                                  fontWeight="700"
                                  fontFamily="system-ui"
                                >
                                  IDR {point.spending.toLocaleString('id-ID')}
                                </text>
                              </g>
                            )}
                          </g>
                        ))}
                      </g>
                    );
                  })()}

                  {/* Chart Title */}
                  <text x="400" y="30" textAnchor="middle" fontSize="20" fill="#1f2937" fontWeight="700" fontFamily="system-ui">
                    Hotel Spending Analysis
                  </text>

                  {/* Y-Axis Label */}
                  <text x="30" y="210" textAnchor="middle" fontSize="14" fill="#6b7280" fontWeight="500" transform="rotate(-90, 30, 210)" fontFamily="system-ui">
                    Amount (IDR)
                  </text>

                  {/* X-Axis Label */}
                  <text x="400" y="420" textAnchor="middle" fontSize="14" fill="#6b7280" fontWeight="500" fontFamily="system-ui">
                    Hotels
                  </text>

                  {/* Legend */}
                  <g transform="translate(620, 70)">
                    <rect x="0" y="0" width="100" height="40" fill="white" stroke="#e5e7eb" strokeWidth="1" rx="6" />
                    <line x1="15" y1="20" x2="35" y2="20" stroke="#8b5cf6" strokeWidth="3" />
                    <circle cx="25" cy="20" r="4" fill="#8b5cf6" />
                    <text x="45" y="25" fontSize="12" fill="#374151" fontFamily="system-ui" fontWeight="500">Spending</text>
                  </g>
                </svg>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <p className="font-semibold text-gray-700 mb-2">No Data Available</p>
                  <p className="text-sm text-gray-500 mb-1">
                    {new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'long' })} {selectedYear}
                  </p>
                  <p className="text-xs text-gray-400">Try selecting a different period</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Department Spending Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl mt-6 border border-light-gray dark:border-gray-700">
          {/* Header Section */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-text-dark dark:text-white mb-1">Department Spending</h3>
              <p className="text-xs text-secondary-dark dark:text-gray-400 italic">All orders (forecast + actual)</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-secondary-dark">
                IDR {topSpendDepartments.reduce((sum, dept) => sum + dept.spent, 0).toLocaleString('id-ID')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                All Departments | {new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'short' })} {selectedYear}
              </p>
            </div>
          </div>

          {/* Hotel, Month/Year Selection */}
          <div className="filter-scroll-mobile items-center gap-3 mb-6 p-3 bg-white dark:bg-gray-700 rounded-lg shadow-md border border-light-gray dark:border-gray-600">
            {/* Hotel Selection */}
            <div className="filter-item-nowrap flex items-center space-x-2">
              <label className="text-xs font-semibold text-secondary-dark dark:text-gray-200 whitespace-nowrap">Hotel:</label>
              <select
                value={selectedDeptHotelFilter}
                onChange={(e) => setSelectedDeptHotelFilter(e.target.value)}
                className="px-2 py-1 text-xs border border-light-gray dark:border-gray-600 rounded focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-200 dark:bg-gray-600 font-medium"
              >
                <option value="">All Hotels</option>
                {hotelList.length > 0 ? (
                  hotelList.map((hotel) => (
                    <option key={hotel.id_hotel} value={hotel.id_hotel}>
                      {hotel.nm_hotel}
                    </option>
                  ))
                ) : (
                  <option disabled>Loading hotels...</option>
                )}
              </select>
            </div>

            {/* Month Selection */}
            <div className="filter-item-nowrap flex items-center space-x-2">
              <label className="text-xs font-semibold text-secondary-dark dark:text-gray-200 whitespace-nowrap">Month:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-2 py-1 text-xs border border-light-gray dark:border-gray-600 rounded focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-200 dark:bg-gray-600 font-medium"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en', { month: 'short' })}
                  </option>
                ))}
              </select>
            </div>

            {/* Year Selection */}
            <div className="filter-item-nowrap flex items-center space-x-2">
              <label className="text-xs font-semibold text-secondary-dark dark:text-gray-200 whitespace-nowrap">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-2 py-1 text-xs border border-light-gray dark:border-gray-600 rounded focus:ring-2 focus:ring-accent-rose focus:border-accent-rose text-primary-dark dark:text-gray-200 dark:bg-gray-600 font-medium"
              >
                {Array.from({ length: 3 }, (_, i) => {
                  const year = new Date().getFullYear() - 1 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Current Selection */}
            <div className="filter-item-nowrap text-xs text-secondary-dark dark:text-gray-300 flex items-center space-x-1">
              <span className="font-semibold whitespace-nowrap">Filter:</span>
              <span className="px-2 py-1 bg-accent-rose/10 text-accent-rose font-medium rounded border border-accent-rose whitespace-nowrap">
                {selectedDeptHotelFilter ?
                  hotelList.find(hotel => hotel.id_hotel == selectedDeptHotelFilter)?.code_hotel || 'Hotel'
                  : 'All'
                } | {new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'short' })} {selectedYear}
              </span>
            </div>
          </div>

          {/* Department Spending Chart */}
          <div className="h-[350px] sm:h-[500px] md:h-[600px] lg:h-[700px] bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-4 overflow-x-auto">
            {topSpendDepartments && topSpendDepartments.length > 0 ? (
              <div className="w-full h-full min-w-[500px]">
                <svg className="w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="xMinYMid meet">
                  {/* Clean Background */}
                  <rect width="1200" height="600" fill="white" />

                  {/* Chart Area Background */}
                  <rect x="100" y="70" width="900" height="360" fill="#fafafa" stroke="#e5e7eb" strokeWidth="1" />

                  {/* Grid Lines */}
                  {[1, 2, 3, 4].map(i => (
                    <line
                      key={`dept-grid-${i}`}
                      x1="100"
                      y1={70 + (i * 90)}
                      x2="1000"
                      y2={70 + (i * 90)}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="3,3"
                    />
                  ))}

                  {/* Vertical Grid Lines */}
                  {topSpendDepartments.slice(0, 5).map((_, i) => {
                    const x = 100 + ((900 / 4) * i);
                    return (
                      <line
                        key={`dept-vgrid-${i}`}
                        x1={x}
                        y1="70"
                        x2={x}
                        y2="430"
                        stroke="#f3f4f6"
                        strokeWidth="1"
                      />
                    );
                  })}

                  {/* Y-Axis */}
                  <line x1="100" y1="70" x2="100" y2="430" stroke="#374151" strokeWidth="2" />

                  {/* X-Axis */}
                  <line x1="100" y1="430" x2="1000" y2="430" stroke="#374151" strokeWidth="2" />

                  {/* Y-Axis Labels */}
                  {(() => {
                    const validDepartments = topSpendDepartments.filter(d => d && !isNaN(d.spent) && d.spent != null);
                    const maxSpending = validDepartments.length > 0 ? Math.max(...validDepartments.map(d => d.spent)) : 1;
                    const validMaxSpending = isNaN(maxSpending) || maxSpending <= 0 ? 1 : maxSpending;
                    return [0, 1, 2, 3, 4].map(i => {
                      const value = (validMaxSpending / 4) * (4 - i);
                      const label = value > 1000000 ? `${(value / 1000000).toFixed(1)}M` :
                        value > 1000 ? `${(value / 1000).toFixed(0)}K` :
                          value.toFixed(0);
                      return (
                        <text
                          key={`dept-y-label-${i}`}
                          x="90"
                          y={80 + (i * 90)}
                          textAnchor="end"
                          fontSize="12"
                          fill="#6b7280"
                          fontFamily="system-ui"
                        >
                          {label}
                        </text>
                      );
                    });
                  })()}

                  {/* Data Line and Points */}
                  {(() => {
                    const departments = topSpendDepartments.slice(0, 5).filter(d => d && !isNaN(d.spent) && d.spent != null);
                    if (departments.length === 0) return null;

                    const maxSpending = departments.length > 0 ? Math.max(...departments.map(d => d.spent)) : 1;
                    const validMaxSpending = isNaN(maxSpending) || maxSpending <= 0 ? 1 : maxSpending;

                    const deptCount = departments.length;
                    const xStep = deptCount > 1 ? 900 / (deptCount - 1) : 0;

                    const points = departments.map((dept, i) => {
                      const validSpent = isNaN(dept.spent) || dept.spent == null ? 0 : Number(dept.spent);
                      const x = deptCount > 1 ? 100 + (xStep * i) : 550; // center jika hanya 1 dept
                      const y = validMaxSpending > 0 ? 430 - ((validSpent / validMaxSpending) * 360) : 430;
                      return {
                        x: isNaN(x) ? 100 : x,
                        y: isNaN(y) ? 430 : y,
                        name: dept.name || 'Unknown',
                        spent: validSpent,
                        orders: dept.orders || 0
                      };
                    });

                    return (
                      <g>
                        {/* Gradient Definition */}
                        <defs>
                          <linearGradient id="deptLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#f59e0b" />
                            <stop offset="100%" stopColor="#ef4444" />
                          </linearGradient>
                          <filter id="deptShadow">
                            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.1" />
                          </filter>
                        </defs>

                        {/* Area under line */}
                        {points.length > 1 && (
                          <path
                            d={`M 100,430 L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${points[points.length - 1].x},430 Z`}
                            fill="url(#deptLineGradient)"
                            fillOpacity="0.1"
                          />
                        )}

                        {/* Main Line */}
                        {points.length > 1 && (
                          <path
                            d={`M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`}
                            fill="none"
                            stroke="url(#deptLineGradient)"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            filter="url(#deptShadow)"
                          />
                        )}

                        {/* Data Points */}
                        {points.map((point, i) => (
                          <g
                            key={`dept-point-${i}`}
                            onMouseEnter={() => setHoveredPoint({ type: 'dept', ...point })}
                            onMouseLeave={() => setHoveredPoint(null)}
                            className="cursor-pointer"
                          >
                            {/* Larger Invisible Area for Easier Hover */}
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r="20"
                              fill="transparent"
                            />

                            {/* Point Shadow */}
                            <circle
                              cx={point.x + 1}
                              cy={point.y + 1}
                              r={hoveredPoint?.name === point.name ? "7" : "5"}
                              fill="#000"
                              fillOpacity="0.1"
                            />

                            {/* Point Circle */}
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r={hoveredPoint?.name === point.name ? "7" : "5"}
                              fill="white"
                              stroke={hoveredPoint?.name === point.name ? "#ef4444" : "#f59e0b"}
                              strokeWidth="3"
                              className="transition-all duration-200"
                            />

                            {/* Inner Point */}
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r="2"
                              fill={hoveredPoint?.name === point.name ? "#ef4444" : "#f59e0b"}
                            />

                            {/* X-Axis Labels */}
                            <text
                              x={point.x}
                              y="470"
                              textAnchor="middle"
                              fontSize="11"
                              fill={hoveredPoint?.name === point.name ? "#ef4444" : "#374151"}
                              fontWeight={hoveredPoint?.name === point.name ? "700" : "600"}
                              fontFamily="system-ui"
                              className="transition-all"
                            >
                              {point.name}
                            </text>

                            {/* Tooltip on Hover */}
                            {hoveredPoint?.name === point.name && (
                              <g>
                                {/* Tooltip Background */}
                                <rect
                                  x={point.x - 90}
                                  y={point.y - 75}
                                  width="180"
                                  height="65"
                                  fill="rgba(0, 0, 0, 0.85)"
                                  rx="8"
                                />
                                <text
                                  x={point.x}
                                  y={point.y - 50}
                                  textAnchor="middle"
                                  fontSize="12"
                                  fill="white"
                                  fontWeight="600"
                                  fontFamily="system-ui"
                                >
                                  {point.name}
                                </text>
                                <text
                                  x={point.x}
                                  y={point.y - 33}
                                  textAnchor="middle"
                                  fontSize="13"
                                  fill="#fcd34d"
                                  fontWeight="600"
                                  fontFamily="system-ui"
                                >
                                  {point.orders} orders
                                </text>
                                <text
                                  x={point.x}
                                  y={point.y - 18}
                                  textAnchor="middle"
                                  fontSize="14"
                                  fill="#fbbf24"
                                  fontWeight="700"
                                  fontFamily="system-ui"
                                >
                                  IDR {point.spent.toLocaleString('id-ID')}
                                </text>
                              </g>
                            )}
                          </g>
                        ))}
                      </g>
                    );
                  })()}

                  {/* Chart Title */}
                  <text x="300" y="25" textAnchor="middle" fontSize="14" fill="#1f2937" fontWeight="700" fontFamily="system-ui">
                    Department Spending
                  </text>

                  {/* Y-Axis Label */}
                  <text x="20" y="150" textAnchor="middle" fontSize="10" fill="#6b7280" fontWeight="500" transform="rotate(-90, 20, 150)" fontFamily="system-ui">
                    Amount (IDR)
                  </text>

                  {/* X-Axis Label */}
                  <text x="300" y="305" textAnchor="middle" fontSize="10" fill="#6b7280" fontWeight="500" fontFamily="system-ui">
                    Departments
                  </text>

                  {/* Legend */}
                  <g transform="translate(450, 50)">
                    <rect x="0" y="0" width="80" height="25" fill="white" stroke="#e5e7eb" strokeWidth="1" rx="4" />
                    <line x1="8" y1="12.5" x2="20" y2="12.5" stroke="#f59e0b" strokeWidth="2" />
                    <circle cx="14" cy="12.5" r="2" fill="#f59e0b" />
                    <text x="25" y="16" fontSize="9" fill="#374151" fontFamily="system-ui">Spending</text>
                  </g>
                </svg>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="font-medium text-gray-600 text-sm">No Department Data</p>
                  <p className="text-xs text-gray-400 mt-1">Loading department statistics...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative max-w-4xl w-full max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setModalImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={modalImage}
              alt="Received Item Full Size"
              className="max-w-full max-h-[80vh] mx-auto object-contain"
            />
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-300">Calculating . . .</p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Dashboard;
